// TELEFONIA da organização, do lado da administração: tudo que vive sob
// /v1/sip/*. O recorte deste módulo é literalmente esse prefixo de rota, que é
// também a fronteira da feature `sip-call` (admin) no back e a mesma restrição
// de papel (diretor/gerente; o back devolve 403 forbidden para vendedor):
//   - ramais (/v1/sip/agents): o número do vendedor no PBX — provisionar,
//     remover, listar, e as credenciais do PRÓPRIO usuário para o softphone;
//   - tronco de saída (/v1/sip/trunk): a config de deploy do Asterisk, o DID
//     (bina) efetivo da org e a sonda de saúde da GSVoIP;
//   - identidade da chamada (/v1/sip/org-identity): o RCD da Origem Verificada,
//     o que a operadora pode exibir no visor de quem recebe;
//   - discagem manual (/v1/sip/manual-dial): o toggle que libera o vendedor a
//     digitar um telefone;
//   - enriquecimento no HubSpot ao discar com IA (/v1/sip/ai-enrich) e o
//     liga/desliga do click to call (/v1/sip/click-to-call): os dois toggles que
//     ficaram para trás quando este módulo nasceu, e que chegaram na onda da IA
//     de voz (veja a nota no fim do arquivo).
// Os seis descrevem a MESMA coisa por ângulos diferentes: de onde a ligação
// sai e com que identidade. O ramal, o tronco e a bina são o caminho; o RCD é o
// nome que viaja nele; os toggles são quem tem permissão de usá-lo e como.
//
// Fica de fora o ÁUDIO da ligação (ringback e URA), que tem rotas próprias e
// mora em ./audio.js — os dois módulos não se importam.
import { api, toApiError } from "./client.js";

// --- Ramais SIP ---
// Espelha a feature `sip-call` (admin) do back (/v1/sip/agents). Gestão restrita
// a diretor/gerente (o back devolve 403 forbidden para vendedor). A senha do
// ramal (secretEnc) NUNCA vem nesta listagem — só é entregue ao provisionar ou
// pelo próprio softphone do vendedor (/v1/sip/agents/me).

export interface SipAgent {
  userId: string;
  sipUsername: string; // número do ramal, ex. "1001"
  enabled: boolean;
  createdAt: string; // ISO
  agentName: string | null; // null se o usuário foi removido
  agentEmail: string | null;
}

export interface SipAgentsResponse {
  agents: SipAgent[];
}

// Credenciais devolvidas ao provisionar — para configurar o softphone. A senha
// vem em claro aqui (uso imediato); não é persistida no Front.
export interface SipCredentials {
  sipUsername: string;
  password: string;
  wssUrl: string;
  realm: string;
}

// GET /v1/sip/agents — ramais provisionados da org.
export async function getSipAgents(): Promise<SipAgentsResponse> {
  const { data, error } = await api<SipAgentsResponse>("/v1/sip/agents");
  if (error) throw toApiError("GET /v1/sip/agents", error);
  return data;
}

// GET /v1/sip/agents/me — credenciais do ramal do PRÓPRIO usuário logado, para
// o softphone. A senha é decifrada no back (SIP_ENC_KEY) e entregue por HTTPS
// autenticado; NUNCA vem do bundle (evita expor o segredo do SIP no navegador).
// O back devolve erro (não-provisionado / sem sessão) quando não há ramal ativo.
export async function getMySipCredentials(): Promise<SipCredentials> {
  const { data, error } = await api<SipCredentials>("/v1/sip/agents/me");
  if (error) throw toApiError("GET /v1/sip/agents/me", error);
  return data;
}

// POST /v1/sip/agents/me — provisiona (ou re-sincroniza) o ramal do próprio
// usuário. Diferente do POST /agents administrativo, nunca aceita um userId do
// navegador: o back deriva a identidade exclusivamente da sessão autenticada.
export async function provisionMySipAgent(): Promise<SipCredentials> {
  const { data, error } = await api<SipCredentials>("/v1/sip/agents/me", {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/sip/agents/me", error);
  return data;
}

// POST /v1/sip/agents { userId } — provisiona (ou re-sincroniza) o ramal do
// vendedor. Idempotente no back: reaproveita número/senha se já existir.
export async function provisionSipAgent(userId: string): Promise<SipCredentials> {
  const { data, error } = await api<SipCredentials>("/v1/sip/agents", {
    method: "POST",
    body: { userId },
  });
  if (error) throw toApiError("POST /v1/sip/agents", error);
  return data;
}

// DELETE /v1/sip/agents/:userId — remove o ramal do vendedor (apaga o endpoint
// no Asterisk e o registro).
export async function deprovisionSipAgent(userId: string): Promise<{ removed: boolean }> {
  const { data, error } = await api<{ removed: boolean }>(`/v1/sip/agents/${userId}`, {
    method: "DELETE",
  });
  if (error) throw toApiError(`DELETE /v1/sip/agents/${userId}`, error);
  return data;
}

// --- Tronco de saída ---
// Espelha GET /v1/sip/trunk (admin). Visão READ-ONLY da config de deploy do
// tronco + o DID (bina) efetivo da org. O back nunca expõe credenciais (ARI
// user/senha, chave de cifra). Restrito a diretor/gerente (403 para vendedor).

export interface SipTrunkInfo {
  endpoint: string; // nome do endpoint pjsip de saída
  provider: string; // ex. "GSVoIP" (derivado do prefixo Tech)
  techPrefix: string; // prefixo do provedor prependido ao número
  authMode: "ip"; // GSVoIP autentica por IP (sem registro)
  orgDid: string | null; // bina configurada para a org
  defaultDid: string | null; // fallback global quando a org não tem DID
  effectiveDid: string | null; // o que sai como caller id
  ringTimeoutSeconds: number;
  enabled: boolean; // worker ARI ligado (SIP_ENABLED)
}

// GET /v1/sip/trunk — config do tronco de saída da org.
export async function getSipTrunk(): Promise<SipTrunkInfo> {
  const { data, error } = await api<SipTrunkInfo>("/v1/sip/trunk");
  if (error) throw toApiError("GET /v1/sip/trunk", error);
  return data;
}

// PUT /v1/sip/trunk/did — define a bina (DID) de saída da org. É o que o back lê
// ao originar (org_no_did); string vazia limpa a bina. Devolve o tronco atualizado.
export async function setSipTrunkDid(callerDid: string): Promise<SipTrunkInfo> {
  const { data, error } = await api<SipTrunkInfo>("/v1/sip/trunk/did", {
    method: "PUT",
    body: { callerDid },
  });
  if (error) throw toApiError("PUT /v1/sip/trunk/did", error);
  return data;
}

// Identidade da marca para RCD (Rich Call Data), a camada de dados ricos da
// Origem Verificada — a implementação brasileira de STIR/SHAKEN (Res. Anatel
// 777/2025). É o que a operadora pode exibir no visor de quem recebe, ao lado do
// número autenticado: quem liga e por quê.
//
// Hoje isto é CADASTRO e nada mais: quem assina a chamada é a prestadora, e a
// especificação de envio da GSVoIP para a AIA ainda não existe do nosso lado.
// O logotipo não está aqui — reusa o `logo` da organização.
export interface OrgIdentity {
  legalName: string;
  cnpj: string;
  displayName: string;
  callReason: string;
}

export async function getOrgIdentity(): Promise<OrgIdentity> {
  const { data, error } = await api<OrgIdentity>("/v1/sip/org-identity");
  if (error) throw toApiError("GET /v1/sip/org-identity", error);
  return data;
}

// PUT /v1/sip/org-identity — o back valida o CNPJ pelo dígito verificador e
// devolve 400 invalid_cnpj quando não fecha; o dígito é a única checagem que
// pega erro de digitação antes de a operadora recusar.
export async function setOrgIdentity(identity: OrgIdentity): Promise<OrgIdentity> {
  const { data, error } = await api<OrgIdentity>("/v1/sip/org-identity", {
    method: "PUT",
    body: identity,
  });
  if (error) throw toApiError("PUT /v1/sip/org-identity", error);
  return data;
}

// Healthcheck do tronco: o BACK sonda a GSVoIP (SIP OPTIONS / pjsip qualify no endpoint
// de saída) e devolve a acessibilidade + latência. O front NÃO fala SIP nem alcança a
// GSVoIP direto (auth por IP, sem CORS) — por isso a verificação mora no back.
export const TRUNK_HEALTH_STATUSES = ["ok", "degraded", "down", "unknown"] as const;
export type TrunkHealthStatus = (typeof TRUNK_HEALTH_STATUSES)[number];

export interface SipTrunkHealth {
  status: TrunkHealthStatus; // ok = alcançável; degraded = lento/instável; down = sem resposta
  reachable: boolean;
  latencyMs: number | null; // RTT do OPTIONS, quando medido
  checkedAt: string; // ISO — quando o back sondou
  detail: string | null; // mensagem técnica opcional (ex.: motivo do down)
}

// GET /v1/sip/trunk/health — status do VoIP (GSVoIP) sondado pelo back.
// Devolve null quando o servidor ainda não expõe o healthcheck (404/501) ou o solicitante
// não pode ver (403): ausência honesta, não erro — a UI mostra "verificação indisponível".
export async function getSipTrunkHealth(): Promise<SipTrunkHealth | null> {
  const { data, error } = await api<SipTrunkHealth>("/v1/sip/trunk/health");
  if (error) {
    if (error.status === 404 || error.status === 501 || error.status === 403) return null;
    throw toApiError("GET /v1/sip/trunk/health", error);
  }
  return data;
}

// --- Discagem manual ---
// Chegou aqui vindo do meio da seção da IA de Voz em api.ts: o par é
// /v1/sip/*, do mesmo grupo de ajustes de telefonia da org, e ler o toggle
// junto com o tronco é o que as telas de Configurações já fazem.

// GET /v1/sip/manual-dial — toggle: habilita a tela de discagem manual
// (vendedor digita um telefone). Desligado, POST /v1/calls sem leadId 409a com
// manual_dial_disabled.
export async function getManualDial(): Promise<{ manualDialEnabled: boolean }> {
  const { data, error } = await api<{ manualDialEnabled: boolean }>("/v1/sip/manual-dial");
  if (error) throw toApiError("GET /v1/sip/manual-dial", error);
  return data;
}

// PUT /v1/sip/manual-dial — liga/desliga a discagem manual.
export async function setManualDial(enabled: boolean): Promise<{ manualDialEnabled: boolean }> {
  const { data, error } = await api<{ manualDialEnabled: boolean }>("/v1/sip/manual-dial", {
    method: "PUT",
    body: { enabled },
  });
  if (error) throw toApiError("PUT /v1/sip/manual-dial", error);
  return data;
}

// Os outros dois toggles /v1/sip/*: enriquecimento do lead ao discar com IA e o
// liga/desliga do click to call. Nasceram no meio da seção da IA de Voz em
// api.ts (por serem lidos pelas telas de lá) e vieram para cá quando aquela
// seção virou ./voice.js: o recorte deste módulo é o prefixo /v1/sip, os dois
// gravam na MESMA tabela (sip_org_settings) e têm a mesma forma do manual-dial
// acima — GET devolve `{ <flag>: boolean }`, PUT recebe `{ enabled }`. Nenhum
// dos dois fala da IA de voz: um é uma consulta ao HubSpot na hora de discar, o
// outro liga um GRUPO INTEIRO de telas no menu.

// GET /v1/sip/ai-enrich — toggle: buscar dados frescos do lead no HubSpot ao
// discar com IA (enriquece {cidade}, {ocupacao}... quando faltam no lead).
export async function getAiEnrich(): Promise<{ hubspotEnrichOnDial: boolean }> {
  const { data, error } = await api<{ hubspotEnrichOnDial: boolean }>("/v1/sip/ai-enrich");
  if (error) throw toApiError("GET /v1/sip/ai-enrich", error);
  return data;
}

// PUT /v1/sip/ai-enrich — liga/desliga o enriquecimento.
export async function setAiEnrich(enabled: boolean): Promise<{ hubspotEnrichOnDial: boolean }> {
  const { data, error } = await api<{ hubspotEnrichOnDial: boolean }>("/v1/sip/ai-enrich", {
    method: "PUT",
    body: { enabled },
  });
  if (error) throw toApiError("PUT /v1/sip/ai-enrich", error);
  return data;
}

// GET /v1/sip/click-to-call — toggle: habilita o click to call na org (as
// telas de auditoria e saúde do click-to-call e o grupo homônimo no menu).
// Desligado, o grupo some da navegação — as rotas seguem válidas por link
// direto. Mesmo formato do manual-dial acima.
export async function getClickToCall(): Promise<{ clickToCallEnabled: boolean }> {
  const { data, error } = await api<{ clickToCallEnabled: boolean }>("/v1/sip/click-to-call");
  if (error) throw toApiError("GET /v1/sip/click-to-call", error);
  return data;
}

// PUT /v1/sip/click-to-call — liga/desliga o click to call.
export async function setClickToCall(enabled: boolean): Promise<{ clickToCallEnabled: boolean }> {
  const { data, error } = await api<{ clickToCallEnabled: boolean }>("/v1/sip/click-to-call", {
    method: "PUT",
    body: { enabled },
  });
  if (error) throw toApiError("PUT /v1/sip/click-to-call", error);
  return data;
}
